import { Box } from "@chakra-ui/react";
import { useRef } from "react";
import { PositionedEvent, CalendarEvent } from "../../types";
import EventDescription from "../EventDescription";

const DRAG_THRESHOLD = 5;

interface EventsLayerProps {
  events: PositionedEvent[];
  timezone: string;
  dragged: PositionedEvent | null;
  resizing: PositionedEvent | null;
  onMouseDown: (e: React.MouseEvent, event: PositionedEvent) => void;
  onResizeStart: (e: React.MouseEvent, event: PositionedEvent) => void;
  onEventClick?: (event: CalendarEvent) => void;
}

export default function EventsLayer({
  events,
  timezone,
  dragged,
  resizing,
  onMouseDown,
  onResizeStart,
  onEventClick,
}: EventsLayerProps) {
  const editingElement = dragged || resizing;
  const thresholdExceededRef = useRef(false);
  const initialPosRef = useRef<{ x: number; y: number } | null>(null);

  return (
    <>
      {events.map((event) => {
        const isEditing = editingElement?.id === event.id;
        const getCursor = () => {
          if (dragged?.id === event.id) return "move";
          return "pointer";
        };

        return (
          <Box
            key={event.id}
            position="absolute"
            top={`${event.top}px`}
            height={`${event.height}px`}
            left={`${event.left * 100}%`}
            width={`${event.width * 100}%`}
            bg={event.style?.bg ?? "blue.500"}
            opacity={isEditing ? 0.6 : (event.style?.opacity || 1)}
            color="white"
            borderRadius="4px"
            border={event?.style?.border || "1px solid #ccc"}
            boxSizing="content-box"
            cursor={getCursor()}
            onClick={(e) => {
              e.stopPropagation();
              if (thresholdExceededRef.current) return;
              onEventClick?.(event);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              thresholdExceededRef.current = false;
              initialPosRef.current = { x: e.clientX, y: e.clientY };
              if (event.canDrag) {
                onMouseDown(e, event);
              }
            }}
            onMouseMove={(e) => {
              if (initialPosRef.current && !thresholdExceededRef.current) {
                const dx = Math.abs(e.clientX - initialPosRef.current.x);
                const dy = Math.abs(e.clientY - initialPosRef.current.y);
                if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
                  thresholdExceededRef.current = true;
                }
              }
            }}
            zIndex={event.zIndex}
          >
            <EventDescription event={event} timezone={timezone} />
            {event.canResize && (
              <Box
                position="absolute"
                bottom={0}
                borderRadius="0 0 4px 4px"
                left={0}
                width="100%"
                height="50%"
                maxHeight="8px"
                bg="clear"
                cursor="ns-resize"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  thresholdExceededRef.current = false;
                  initialPosRef.current = { x: e.clientX, y: e.clientY };
                  onResizeStart(e, event);
                }}
              />
            )}
          </Box>
        );
      })}
    </>
  );
}
